import { TabPanel, TabPanelProps } from "../../components/TabPanel.tsx";
import { useCallback, useEffect, useState } from "react";
import { fetchWithError } from "../../utils/fetchWithError.ts";
import {
    Box,
    CircularProgress,
    FormControlLabel,
    FormLabel,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type {
    IUserSettings,
    IUserSettingsGlobal,
    UserTheme
} from "../../../../src/core/types/settings/user-settings.interface";
import { useTheme as useAppTheme } from "../../theme/ThemeContext";
import { useSnackbar } from "notistack";
import {
    getEffectiveUiLanguage,
    isUiLanguageSupported,
} from "../../config/supportedLanguages";

interface SettingsTabProps extends TabPanelProps {
    onGlobalSettingsChanged?: () => void;
}

export const SettingsTab = (props: SettingsTabProps) => {
    const { onGlobalSettingsChanged, ...tabPanelProps } = props;
    const { setColorMode, mode } = useAppTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [settings, setSettings] = useState<IUserSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const applyPatch = useCallback(
        async (patch: Partial<IUserSettingsGlobal>) => {
            setSaving(true);
            try {
                const updated = await fetchWithError<IUserSettings>("/session/settings/global", {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(patch)
                });
                setSettings(updated);
                if (patch.language != null) {
                    document.documentElement.lang = getEffectiveUiLanguage(patch.language);
                }
                onGlobalSettingsChanged?.();
            } catch (e) {
                enqueueSnackbar(
                    e instanceof Error ? e.message : "Einstellungen konnten nicht gespeichert werden.",
                    { variant: "error" }
                );
            } finally {
                setSaving(false);
            }
        },
        [enqueueSnackbar, onGlobalSettingsChanged]
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchWithError<IUserSettings>("/session/settings", {
                    credentials: "include"
                });
                if (cancelled || !data) {
                    if (!cancelled) setSettings(null);
                    return;
                }
                const lang = data.global?.language;
                if (lang != null && !isUiLanguageSupported(lang)) {
                    try {
                        const migrated = await fetchWithError<IUserSettings>("/session/settings/global", {
                            method: "PUT",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ language: getEffectiveUiLanguage(lang) })
                        });
                        if (!cancelled) {
                            setSettings(migrated);
                            document.documentElement.lang = getEffectiveUiLanguage(migrated.global?.language);
                        }
                    } catch {
                        if (!cancelled) setSettings(data);
                    }
                } else {
                    if (!cancelled) {
                        setSettings(data);
                        document.documentElement.lang = getEffectiveUiLanguage(data.global?.language);
                    }
                }
            } catch {
                if (!cancelled) setSettings(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const g = settings?.global;

    return (
        <TabPanel {...tabPanelProps}>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : !settings ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <Typography color="text.secondary" textAlign="center">
                        Einstellungen konnten nicht geladen werden.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={3} sx={{ maxWidth: 480, width: "100%", mx: "auto" }}>
                    <Box>
                        <FormLabel id="settings-theme-label" sx={{ display: "block", mb: 1 }}>
                            Erscheinungsbild
                        </FormLabel>
                        <ToggleButtonGroup
                            exclusive
                            value={mode}
                            onChange={(_, v: UserTheme | null) => {
                                if (v) {
                                    setColorMode(v);
                                    onGlobalSettingsChanged?.();
                                }
                            }}
                            disabled={saving}
                            aria-labelledby="settings-theme-label"
                            size="small"
                            fullWidth
                            color="primary"
                            sx={(theme) => ({
                                gap: 0.5,
                                "& .MuiToggleButton-root": {
                                    textTransform: "none",
                                    fontWeight: 600,
                                    flex: 1,
                                    color: theme.palette.text.primary,
                                    borderColor: theme.palette.divider,
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? alpha(theme.palette.common.white, 0.06)
                                            : alpha(theme.palette.common.black, 0.05),
                                    "&:hover": {
                                        backgroundColor:
                                            theme.palette.mode === "dark"
                                                ? alpha(theme.palette.common.white, 0.1)
                                                : alpha(theme.palette.common.black, 0.08)
                                    },
                                    "&.Mui-selected": {
                                        color: theme.palette.primary.contrastText,
                                        backgroundColor: theme.palette.primary.main,
                                        borderColor: theme.palette.primary.main,
                                        "&:hover": {
                                            color: theme.palette.primary.contrastText,
                                            backgroundColor: theme.palette.primary.dark,
                                            borderColor: theme.palette.primary.dark
                                        }
                                    },
                                    "&.Mui-disabled": {
                                        borderColor: theme.palette.action.disabledBackground
                                    }
                                }
                            })}
                        >
                            <ToggleButton value="light">Hell</ToggleButton>
                            <ToggleButton value="dark">Dunkel</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

{/*                     <FormControl fullWidth size="small">
                        <InputLabel id="settings-lang-label">Sprache</InputLabel>
                        <Select<SupportedUiLanguageCode>
                            labelId="settings-lang-label"
                            label="Sprache"
                            value={getEffectiveUiLanguage(g?.language)}
                            onChange={(e) =>
                                void applyPatch({
                                    language: e.target.value as SupportedUiLanguageCode
                                })
                            }
                            disabled={saving}
                        >
                            {UI_LANGUAGES.map(({ code, label }) => (
                                <MenuItem key={code} value={code}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl> */}

                    <FormControlLabel
                        control={
                            <Switch
                                checked={g?.receiveMessageEmails ?? true}
                                onChange={(e) =>
                                    void applyPatch({
                                        receiveMessageEmails: e.target.checked
                                    })
                                }
                                disabled={saving}
                            />
                        }
                        label="E-Mail bei neuen Nachrichten"
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={g?.showProfileTooltip ?? true}
                                onChange={(e) => void applyPatch({ showProfileTooltip: e.target.checked })}
                                disabled={saving}
                            />
                        }
                        label="Hinweise auf dem Dashboard anzeigen"
                    />
                </Stack>
            )}
        </TabPanel>
    );
};
